import React, { useState } from 'react';
import axios from 'axios';
import ReferenceUpload from './ReferenceUpload';
import MultiImageSlot from './MultiImageSlot';
import AssetLibraryPickerModal from '../asset-library/AssetLibraryPickerModal';

/**
 * BrandAssetsCard
 * ===============
 * Unified card with 3 reference-upload slots — Product, Avatar, Logo.
 * Each slot is colour-coded to match the "References Used" chips in the
 * View Details screen so users connect "what I upload here" with
 * "what gets sent to the AI".
 *
 * Slot details:
 *   - Product: file picker (single image used as the primary product
 *              reference). Persists to `product_image` AND first slot
 *              of `product_images[]` for backward compat with video flows.
 *   - Avatar:  file picker (single portrait). Persists to `avatar_image_url`.
 *   - Logo:    file picker + optional "Fetch from website URL" button
 *              (calls /api/extract-logo-only). Persists to `brand_logo_url`.
 *
 * Props:
 *   formData      — current form state (must include product_image,
 *                   product_images, avatar_image_url, brand_logo_url, website_url)
 *   setFormData   — state setter
 *   sessionToken  — for the logo fetch API call
 *   testIdPrefix  — defaults to 'brand-assets'
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENTS = {
  product: {
    ring: 'focus:ring-blue-500',
    border: 'border-blue-300 dark:border-blue-700',
    badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300',
    dot: 'bg-blue-500',
    text: 'text-blue-600 dark:text-blue-300',
  },
  avatar: {
    ring: 'focus:ring-purple-500',
    border: 'border-purple-300 dark:border-purple-700',
    badge: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300',
    dot: 'bg-purple-500',
    text: 'text-purple-600 dark:text-purple-300',
  },
  logo: {
    ring: 'focus:ring-emerald-500',
    border: 'border-emerald-300 dark:border-emerald-700',
    badge: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    text: 'text-emerald-600 dark:text-emerald-300',
  },
};

const IconPackage = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);
const IconUser = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);
const IconSparkles = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.5 7H21l-6 4.5L17 21l-6-4.5L5 21l2-7.5L1 9h7.5z" />
  </svg>
);

export default function BrandAssetsCard({ formData, setFormData, sessionToken, testIdPrefix = 'brand-assets' }) {
  const [fetchingLogo, setFetchingLogo] = useState(false);
  const [logoFetchError, setLogoFetchError] = useState('');
  // Which library kind is the picker currently open for? (null = closed)
  const [pickerKind, setPickerKind] = useState(null);

  const setProductImages = (urls) => {
    const arr = Array.isArray(urls) ? urls.filter(Boolean) : [];
    setFormData((prev) => ({
      ...prev,
      // Keep both fields in sync — `product_image` is the primary single ref
      // (used by image-promo flows) and `product_images` is the array used
      // by video transition mode (up to 2 images).
      product_image: arr[0] || '',
      product_images: arr,
    }));
  };

  const setAvatars = (urls) => {
    const arr = Array.isArray(urls) ? urls.filter(Boolean) : [];
    setFormData((prev) => ({
      ...prev,
      // Mirror to legacy singular field so every downstream single-avatar
      // consumer (Veo image_reference, older regeneration paths) keeps
      // working unchanged. `avatar_image_urls` is the source of truth
      // for multi-reference video / multi-ref image generators.
      avatar_image_url: arr[0] || '',
      avatar_image_urls: arr,
    }));
  };

  const setLogo = (url) => {
    setFormData((prev) => ({ ...prev, brand_logo_url: url || '' }));
  };

  const handleFetchLogo = async () => {
    setLogoFetchError('');
    const url = (formData.website_url || '').trim();
    if (!url) {
      setLogoFetchError('Enter a website URL above first, then click here.');
      return;
    }
    setFetchingLogo(true);
    try {
      const resp = await axios.post(
        `${API}/extract-logo-only`,
        { url },
        { headers: { Authorization: `Bearer ${sessionToken}` }, timeout: 25000 }
      );
      if (resp.data?.success && resp.data?.logo_url) {
        setLogo(resp.data.logo_absolute_url || resp.data.logo_url);
      } else {
        setLogoFetchError(resp.data?.error || 'No logo found.');
      }
    } catch (e) {
      setLogoFetchError(e.response?.data?.detail || 'Logo fetch failed.');
    } finally {
      setFetchingLogo(false);
    }
  };

  const fetchBtn = (
    <div data-testid={`${testIdPrefix}-logo-fetch-row`}>
      <button
        type="button"
        onClick={handleFetchLogo}
        disabled={fetchingLogo}
        className="w-full text-[10px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-lg py-1.5 px-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        data-testid={`${testIdPrefix}-fetch-logo-btn`}
      >
        {fetchingLogo ? 'Fetching logo…' : '🔗 Fetch logo from website URL'}
      </button>
      {logoFetchError && (
        <p className="mt-1 text-[10px] text-red-500" data-testid={`${testIdPrefix}-logo-fetch-error`}>
          {logoFetchError}
        </p>
      )}
    </div>
  );

  return (
    <div
      className="mt-4 rounded-2xl bg-white/60 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-200 dark:border-slate-700 p-5"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-baseline justify-between mb-1">
        <h4 className="text-sm font-semibold text-slate-800 dark:text-white">Brand Assets</h4>
        <span className="text-[10px] text-slate-400 dark:text-slate-500">Sent to AI as references</span>
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-4">
        Each slot tells the AI a different thing — preserve your <span className="font-medium text-blue-600 dark:text-blue-300">product</span>,
        embed your <span className="font-medium text-purple-600 dark:text-purple-300">portrait</span> as the hero,
        and place your <span className="font-medium text-emerald-600 dark:text-emerald-300">logo</span> on every generation.
      </p>

      {/* Library pickers — one per slot. Voices extracted from websites
          auto-save to the Asset Library so users don't re-extract. */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid={`${testIdPrefix}-library-row`}>
        <button
          type="button"
          onClick={() => setPickerKind('product')}
          data-testid={`${testIdPrefix}-pick-product`}
          className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
        >
          📚 Pick product from library
        </button>
        <button
          type="button"
          onClick={() => setPickerKind('avatar')}
          data-testid={`${testIdPrefix}-pick-avatar`}
          className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50"
        >
          📚 Pick avatar from library
        </button>
        <button
          type="button"
          onClick={() => setPickerKind('logo')}
          data-testid={`${testIdPrefix}-pick-logo`}
          className="text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50"
        >
          📚 Pick logo from library
        </button>
      </div>

      <AssetLibraryPickerModal
        isOpen={!!pickerKind}
        onClose={() => setPickerKind(null)}
        kind={pickerKind || 'product'}
        allowMultiple={pickerKind !== 'logo'}
        onConfirm={(picked) => {
          const urls = picked.map(p => p.s3_url).filter(Boolean);
          if (pickerKind === 'product') {
            // Append to existing product images (max 7), keep uniques
            const current = Array.isArray(formData.product_images) && formData.product_images.length > 0
              ? formData.product_images
              : (formData.product_image ? [formData.product_image] : []);
            const merged = [...current];
            for (const u of urls) if (u && !merged.includes(u)) merged.push(u);
            setProductImages(merged.slice(0, 7));
          } else if (pickerKind === 'avatar') {
            const current = Array.isArray(formData.avatar_image_urls) && formData.avatar_image_urls.length > 0
              ? formData.avatar_image_urls
              : (formData.avatar_image_url ? [formData.avatar_image_url] : []);
            const merged = [...current];
            for (const u of urls) if (u && !merged.includes(u)) merged.push(u);
            setAvatars(merged.slice(0, 7));
          } else if (pickerKind === 'logo') {
            if (urls[0]) setLogo(urls[0]);
          }
        }}
      />

      <div className="flex flex-col md:flex-row gap-4">
        <MultiImageSlot
          label="Product Image"
          helpText="Photo of your product or brand visual. Add up to 7 (for transition/multi-ref videos)."
          values={
            Array.isArray(formData.product_images) && formData.product_images.length > 0
              ? formData.product_images
              : (formData.product_image ? [formData.product_image] : [])
          }
          onChange={setProductImages}
          accent={ACCENTS.product}
          icon={<IconPackage />}
          testIdPrefix={`${testIdPrefix}-product`}
          maxFiles={7}
          addMoreLabel="+ Add another product (for transition / multi-ref videos)"
        />
        <MultiImageSlot
          label="Your Avatar"
          helpText="Your portrait(s) — appears as the hero. Add up to 7 for multi-persona / cinematic sets."
          values={
            Array.isArray(formData.avatar_image_urls) && formData.avatar_image_urls.length > 0
              ? formData.avatar_image_urls
              : (formData.avatar_image_url ? [formData.avatar_image_url] : [])
          }
          onChange={setAvatars}
          accent={ACCENTS.avatar}
          icon={<IconUser />}
          testIdPrefix={`${testIdPrefix}-avatar`}
          maxFiles={7}
          addMoreLabel="+ Add another avatar (multi-persona videos)"
        />
        <ReferenceUpload
          label="Brand Logo"
          helpText="Upload or fetch from your website URL."
          value={formData.brand_logo_url}
          onChange={setLogo}
          accent={ACCENTS.logo}
          icon={<IconSparkles />}
          testIdPrefix={`${testIdPrefix}-logo`}
          thumbContain
          extraAction={fetchBtn}
        />
      </div>
    </div>
  );
}
